import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../Button";
import { useAuth } from "../../context/AuthContext";
import { images } from "../../config/images";
import { ChevronDown, CreditCard, History, LogOut, Menu, User, X } from "lucide-react";
import Dialog from "../Dialog";
import { getMyCoursePurchases } from "../../api";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [hasPurchasedCourses, setHasPurchasedCourses] = useState(false);
  const profileMenuRef = useRef(null);

  const confirmLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
    setProfileMenuOpen(false);
    setLogoutModalOpen(false);
  };

  const isActive = (path) => location.pathname === path;
  const userDisplayName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.name || "User";
  const userDisplayEmail = user?.email || "";

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Courses", path: user ? "/user/browse" : "/courses" },
    { name: "Sharekhan", path: "/broker/sharekhan" },
    { name: "Basic Quiz", path: "/quiz" },
    { name: "Career", path: "/career" },
  ];

  const userCourseTabs = [
    { name: "My Courses", path: "/user/courses" },
  ];
  const visibleUserCourseTabs = hasPurchasedCourses
    ? userCourseTabs
    : [];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id || user.role === "admin") {
        if (!cancelled) setHasPurchasedCourses(false);
        return;
      }

      const localPurchased = Array.isArray(user.purchasedCourseIds)
        ? user.purchasedCourseIds
        : [];
      if (localPurchased.length > 0) {
        if (!cancelled) setHasPurchasedCourses(true);
        return;
      }

      try {
        const data = await getMyCoursePurchases();
        const purchasedIds = Array.isArray(data?.purchasedCourseIds)
          ? data.purchasedCourseIds
          : [];
        if (!cancelled) setHasPurchasedCourses(purchasedIds.length > 0);
      } catch {
        if (!cancelled) setHasPurchasedCourses(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.role, user?.purchasedCourseIds]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;

    const onMouseDown = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [profileMenuOpen]);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 py-3 flex justify-between items-center">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center justify-center gap-2 4xl:gap-4"
        >
          <img src={images.logo} alt="Logo" className="h-10 w-auto 4xl:h-14" />
          <span className="text-lg font-bold text-primary-600 mt-4 4xl:text-2xl">
            Market Mavericks
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6 4xl:gap-12">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-1.5 rounded-lg font-semibold transition 4xl:text-xl ${
                isActive(link.path)
                  ? "bg-primary-100 text-primary-600"
                  : "text-gray-700 hover:text-primary-600 hover:bg-primary-50"
              }`}
            >
              {link.name}
            </Link>
          ))}

          {user && user.role !== "admin" &&
            visibleUserCourseTabs.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-1.5 rounded-lg font-semibold transition 4xl:text-xl ${
                  location.pathname.startsWith(link.path)
                    ? "bg-primary-100 text-primary-600"
                    : "text-gray-700 hover:text-primary-600 hover:bg-primary-50"
                }`}
              >
                {link.name}
              </Link>
            ))}

          <div className="flex items-center gap-3 4xl:gap-6">
            {user ? (
              <>
                {user.role === "admin" ? (
                  <>
                    <Link to="/admin">
                      <Button variant="ghost" size="sm">
                        Admin Panel
                      </Button>
                    </Link>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setLogoutModalOpen(true)}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <div className="relative" ref={profileMenuRef}>
                    <button
                      type="button"
                      onClick={() => setProfileMenuOpen((prev) => !prev)}
                      className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-primary-200 hover:text-primary-600 transition"
                    >
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-600 overflow-hidden">
                        {(user.profileImage || user.profile_image) ? (
                          <img
                            src={user.profileImage || user.profile_image}
                            alt={userDisplayName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User size={16} />
                        )}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${profileMenuOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {profileMenuOpen && (
                      <div className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-100 bg-white p-2 shadow-xl">
                        <div className="mb-2 rounded-lg bg-gray-50 px-3 py-2">
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {userDisplayName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {userDisplayEmail}
                          </p>
                        </div>
                        <Link
                          to="/user/profile"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                        >
                          <User size={16} />
                          Profile
                        </Link>
                        <Link
                          to="/user/subscription"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                        >
                          <CreditCard size={16} />
                          Subscription
                        </Link>
                        <Link
                          to="/user/payment-history"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                        >
                          <History size={16} />
                          Payment History
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setProfileMenuOpen(false);
                            setLogoutModalOpen(true);
                          }}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                        >
                          <LogOut size={16} />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="primary" size="sm">
                    Register
                  </Button>
                </Link>
              </>
            )}
          </div>
        </nav>

        {/* Hamburger */}
        <button className="lg:hidden" onClick={() => setIsOpen(true)}>
          <Menu size={28} />
        </button>
      </div>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Right Side Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-white shadow-xl z-50 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } lg:hidden`}
      >
        {/* Drawer Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <span className="font-bold text-lg text-primary-600">Menu</span>
          <button onClick={() => setIsOpen(false)}>
            <X size={24} />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="px-6 py-6 flex flex-col gap-4">
          {/* Nav Links */}
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className={`px-3 py-2 rounded-lg font-semibold ${
                isActive(link.path)
                  ? "bg-primary-100 text-primary-600"
                  : "text-gray-700 hover:text-primary-600"
              }`}
            >
              {link.name}
            </Link>
          ))}

          {user && user.role !== "admin" &&
            visibleUserCourseTabs.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`px-3 py-2 rounded-lg font-semibold ${
                  location.pathname.startsWith(link.path)
                    ? "bg-primary-100 text-primary-600"
                    : "text-gray-700 hover:text-primary-600"
                }`}
              >
                {link.name}
              </Link>
            ))}

          {/* Auth Section (Career ke niche) */}
          <div className="border-t pt-4 mt-2">
            {user ? (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {userDisplayName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {userDisplayEmail}
                  </p>
                </div>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full">
                      Admin Panel
                    </Button>
                  </Link>
                )}
                {user.role !== "admin" && (
                  <>
                    <Link to="/user/profile" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full">
                        Profile
                      </Button>
                    </Link>
                    <Link to="/user/subscription" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full">
                        Subscription
                      </Button>
                    </Link>
                    <Link to="/user/payment-history" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full">
                        Payment History
                      </Button>
                    </Link>
                  </>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setLogoutModalOpen(true)}
                  className="w-full"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" size="sm" className="w-full">
                    Login
                  </Button>
                </Link>

                <Link to="/register" onClick={() => setIsOpen(false)}>
                  <Button variant="primary" size="sm" className="w-full">
                    Register
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title="Confirm Logout"
        size="md"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <Button size="sm" onClick={() => setLogoutModalOpen(false)} >
              No
            </Button>
            <Button variant="primary" size="sm" onClick={confirmLogout}>
              Yes
            </Button>
          </div>
        }
      >
        <p className="text-gray-600">
          Are you sure you want to log out of your account?
        </p>
      </Dialog>
    </header>
  );
};

export default Header;
